import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { fmtMonthDay, type HealthKind, type ProjectStatus, type RadarProjectDTO } from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { useHotkeys } from '../../lib/hotkeys';
import { useLayout } from '../../lib/media';
import { useMe, useMembers, useProjects } from '../../lib/queries';
import { Icon, StatusIcon, type IconName } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/menu';
import { Avatar, Button, EmptyState, Hint, IconButton, Skeleton, Toggle } from '../../ui/primitives';
import { ProjectFormModal } from '../projects/ProjectFormModal';
import { ProjectImportModal } from '../projects/ProjectImportModal';
import './radar.css';

// 项目雷达（设计稿 7a）：按状态分组的列表，健康信号只在异常时出现，“完成”组默认折叠。
// 按团队分组（14b）时每组最多显示 5 行；1024–1279 宽度隐藏“最近进展”列（23c）。

type GroupBy = 'status' | 'team' | 'owner';
type View = 'list' | 'board';
type SignalFilter = 'any' | Exclude<HealthKind, 'owner_on_leave'>;

const STATUS_GROUPS: { status: ProjectStatus; label: string }[] = [
  { status: 'blocked', label: '阻塞' },
  { status: 'progress', label: '进行中' },
  { status: 'todo', label: '未开始' },
  { status: 'done', label: '完成' },
];

const SIGNAL_ITEMS: { value: SignalFilter; label: string }[] = [
  { value: 'any', label: '有任何异常信号' },
  { value: 'blocked', label: '阻塞' },
  { value: 'stalled', label: '停滞' },
  { value: 'due_slipped', label: '日期反复延期' },
  { value: 'late_start', label: '晚启动' },
];

/** 详情页的“2 / 13”和上一个 / 下一个，按用户刚才看的雷达顺序 */
export let lastRadarOrder: string[] = [];

const signals = (p: RadarProjectDTO) => p.health.filter((h) => h.kind !== 'owner_on_leave');

function load<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function save(key: string, v: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* 忽略 */
  }
}

interface Group {
  key: string;
  label: string;
  icon: ReactNode;
  items: RadarProjectDTO[];
  folded?: boolean;
  meta?: ReactNode;
}

export function RadarPage({ scope }: { scope: 'all' | 'mine' }) {
  const { id: teamRoute } = useParams();
  const navigate = useNavigate();
  const layout = useLayout();
  const mobile = layout === 'mobile';
  const { data: me } = useMe();
  const { data: members = [] } = useMembers();
  const { data: projects, isLoading } = useProjects();
  const [owners, setOwners] = useState<Set<string>>(new Set());
  const [teams, setTeams] = useState<Set<string>>(new Set(teamRoute ? [teamRoute] : []));
  const [sig, setSig] = useState<Set<SignalFilter>>(new Set());
  const [view, setView] = useState<View>(() => load('wd-radar-view', 'list'));
  const [groupBy, setGroupBy] = useState<GroupBy>(() => load('wd-radar-group', 'status'));
  const [showDone, setShowDone] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [sel, setSel] = useState(-1);
  const [menu, setMenu] = useState<'owner' | 'team' | 'signal' | 'display' | null>(null);
  const ownerBtn = useRef<HTMLButtonElement>(null);
  const teamBtn = useRef<HTMLButtonElement>(null);
  const sigBtn = useRef<HTMLButtonElement>(null);
  const displayBtn = useRef<HTMLButtonElement>(null);

  // 新建项目：顶栏按钮、空状态，或从命令面板带 ?new=1 过来
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  useEffect(() => {
    if (params.get('new') === '1' || params.get('import') === '1') {
      if (params.get('new') === '1') setCreating(true);
      else setImporting(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);
  const createModal = (
    <>
      {creating && <ProjectFormModal defaultTeamId={teamRoute ?? null} onClose={() => setCreating(false)} />}
      {importing && me?.role === 'admin' && <ProjectImportModal onClose={() => setImporting(false)} />}
    </>
  );
  const emptyMineDesc = '新建一个项目，或在「我 › 负责的项目」里加入已有的项目，日报会按项目预填。';

  useEffect(() => setTeams(new Set(teamRoute ? [teamRoute] : [])), [teamRoute]);
  useEffect(() => save('wd-radar-view', view), [view]);
  useEffect(() => save('wd-radar-group', groupBy), [groupBy]);

  const filtered = useMemo(() => {
    // 看不到内容的项目（设置「项目可见范围」）不进雷达和团队页
    let list = (projects ?? []).filter((p) => p.visible);
    if (scope === 'mine') list = list.filter((p) => p.mine);
    if (owners.size) list = list.filter((p) => owners.has(p.ownerId));
    if (teams.size) list = list.filter((p) => p.teamId && teams.has(p.teamId));
    if (sig.size) {
      list = list.filter((p) => {
        const kinds = signals(p).map((h) => h.kind);
        return (sig.has('any') && kinds.length > 0) || kinds.some((k) => sig.has(k as SignalFilter));
      });
    }
    return list;
  }, [projects, scope, owners, teams, sig]);

  const byPriority = (a: RadarProjectDTO, b: RadarProjectDTO) =>
    signals(b).length - signals(a).length || (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999') || a.code.localeCompare(b.code);

  const groups: Group[] = useMemo(() => {
    if (groupBy === 'status') {
      return STATUS_GROUPS.map((g) => {
        const items = filtered.filter((p) => p.status === g.status).sort(byPriority);
        const recent = g.status === 'done' ? [...items].sort((a, b) => (b.doneAt ?? '').localeCompare(a.doneAt ?? ''))[0] : undefined;
        return {
          key: g.status,
          label: g.label,
          icon: <StatusIcon status={g.status} />,
          items,
          folded: g.status === 'done' && !showDone,
          meta: recent ? `最近：${recent.code} ${recent.name}${recent.doneAt ? ` · ${fmtMonthDay(recent.doneAt)}` : ''}` : undefined,
        };
      }).filter((g) => g.items.length > 0);
    }
    // 按团队 / 负责人分组：已完成和没有信号的未开始项目折叠到底部说明里
    const visible = filtered.filter((p) => showDone || (p.status !== 'done' && !(p.status === 'todo' && signals(p).length === 0)));
    const keyOf = (p: RadarProjectDTO) => (groupBy === 'team' ? (p.teamId ?? 'none') : p.ownerId);
    const keys = [...new Set(visible.map(keyOf))];
    const order = groupBy === 'team' ? (me?.teams.map((t) => t.id) ?? []) : members.map((m) => m.id);
    keys.sort((a, b) => (order.indexOf(a) + 1 || 999) - (order.indexOf(b) + 1 || 999));
    return keys.map((k) => {
      const items = visible.filter((p) => keyOf(p) === k).sort(byPriority);
      const team = me?.teams.find((t) => t.id === k);
      const member = members.find((m) => m.id === k);
      return {
        key: k,
        label: groupBy === 'team' ? (team?.name ?? '未分配团队') : (member?.name ?? '未知'),
        icon: groupBy === 'team' ? <span className="wd-team-chip">{team?.initial ?? '—'}</span> : <Avatar initial={member?.initial ?? '?'} seed={member?.name} />,
        items,
      };
    });
  }, [filtered, groupBy, showDone, me?.teams, members]);

  const foldedNote = useMemo(() => {
    if (groupBy === 'status' || showDone) return null;
    const done = filtered.filter((p) => p.status === 'done').length;
    const idle = filtered.filter((p) => p.status === 'todo' && signals(p).length === 0).length;
    if (!done && !idle) return null;
    return `另有 ${[done ? `${done} 个已完成` : '', idle ? `${idle} 个未开始` : ''].filter(Boolean).join('、')}的项目已折叠 · 每组最多显示 5 行`;
  }, [filtered, groupBy, showDone]);

  // 键盘顺序 = 当前可见行
  const visibleRows = useMemo(() => {
    const rows: RadarProjectDTO[] = [];
    for (const g of groups) {
      if (g.folded || collapsed.has(g.key)) continue;
      const limited = groupBy !== 'status' && !expandedTeams.has(g.key) ? g.items.slice(0, 5) : g.items;
      rows.push(...limited);
    }
    return rows;
  }, [groups, collapsed, groupBy, expandedTeams]);
  useEffect(() => {
    lastRadarOrder = visibleRows.map((p) => p.id);
  }, [visibleRows]);

  const open = (p: RadarProjectDTO) => {
    lastRadarOrder = visibleRows.map((x) => x.id);
    navigate(`/projects/${p.id}`);
  };

  useHotkeys(
    {
      j: () => setSel((s) => Math.min(s + 1, visibleRows.length - 1)),
      ArrowDown: () => setSel((s) => Math.min(s + 1, visibleRows.length - 1)),
      k: () => setSel((s) => Math.max(s - 1, 0)),
      ArrowUp: () => setSel((s) => Math.max(s - 1, 0)),
      Enter: () => visibleRows[sel] && open(visibleRows[sel]),
      f: () => setMenu('owner'),
      'mod+b': () => setView((v) => (v === 'list' ? 'board' : 'list')),
    },
    !mobile,
  );

  const total = (projects ?? []).filter((p) => p.visible && (scope === 'mine' ? p.mine : true) && p.status !== 'cancelled').length;
  const withSignals = (projects ?? []).filter((p) => p.visible && (scope === 'mine' ? p.mine : true) && signals(p).length > 0).length;
  const title = teamRoute ? (me?.teams.find((t) => t.id === teamRoute)?.name ?? '团队') : scope === 'mine' ? '项目' : '雷达';
  const anyFilter = owners.size + sig.size + (teamRoute ? 0 : teams.size) > 0;

  const toggleIn = <T,>(set: Set<T>, v: T, apply: (s: Set<T>) => void) => {
    const n = new Set(set);
    if (n.has(v)) n.delete(v);
    else n.add(v);
    apply(n);
  };

  if (isLoading || !projects) {
    return (
      <div style={{ padding: 24 }} aria-busy="true">
        <Skeleton w={200} h={14} />
      </div>
    );
  }

  if (mobile) {
    return (
      <div className="rd-m">
        {createModal}
        <div className="rd-m-head">
          <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
            <div className="row" style={{ gap: 4 }}>
              <div className="t-page-title">{title}</div>
              <IconButton mobile name="plus" label="新建项目" onClick={() => setCreating(true)} />
            </div>
            {!teamRoute && (
              <div className="rd-seg" role="tablist" aria-label="范围">
                <button type="button" role="tab" aria-selected={scope === 'mine'} className={clsx(scope === 'mine' && 'on')} onClick={() => navigate('/projects', { replace: true })}>
                  我参与的
                </button>
                <button type="button" role="tab" aria-selected={scope === 'all'} className={clsx(scope === 'all' && 'on')} onClick={() => navigate('/radar', { replace: true })}>
                  全部
                </button>
              </div>
            )}
          </div>
          <div className="t-meta" style={{ marginTop: 6 }}>
            {total} 个项目{withSignals ? ` · ${withSignals} 个有异常信号` : ''}
          </div>
        </div>
        {filtered.length === 0 && (
          <EmptyState icon="grid" title={scope === 'mine' ? '你还没有参与的项目' : '没有符合条件的项目'} desc={scope === 'mine' ? emptyMineDesc : undefined}>
            {scope === 'mine' && (
              <Button mobile variant="primary" icon="plus" onClick={() => setCreating(true)}>
                新建项目
              </Button>
            )}
          </EmptyState>
        )}
        {groups.map((g) => (
          <div key={g.key}>
            <button type="button" className="rd-m-group" onClick={() => (g.key === 'done' ? setShowDone(!showDone) : toggleIn(collapsed, g.key, setCollapsed))}>
              <Icon name={g.folded || collapsed.has(g.key) ? 'chevR' : 'chevD'} size={12} />
              {g.icon}
              <span className="name">{g.label}</span>
              <span>{g.items.length}</span>
            </button>
            {!g.folded &&
              !collapsed.has(g.key) &&
              g.items.map((p) => (
                <button key={p.id} type="button" className="rd-m-row" onClick={() => open(p)}>
                  <StatusIcon status={p.status} />
                  <span className="info">
                    <span className="title">
                      <span className="name">{p.name}</span>
                      <span className="code">{p.code}</span>
                    </span>
                    <span className={clsx('desc', !p.latest && 'none')}>{p.latest?.text ?? '暂无进展'}</span>
                  </span>
                  {signals(p)[0] ? <span className="health">{signals(p)[0].label}</span> : p.dueDate ? <span className="due">{fmtMonthDay(p.dueDate)}</span> : null}
                </button>
              ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {createModal}
      <TopBar crumbs={[<span key="t" className="row" style={{ gap: 8 }}><span className="c1">{title}</span><span className="t-meta">{total} 个项目{withSignals ? ` · ${withSignals} 个有异常信号` : ''}</span></span>]}>
        {me?.role === 'admin' && (
          <Button icon="export" onClick={() => setImporting(true)}>
            导入
          </Button>
        )}
        <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
          新建项目
        </Button>
      </TopBar>

      <div className="rd-toolbar">
        <div className="row" style={{ gap: 6 }}>
          <Chip refEl={ownerBtn} icon="user" label="负责人" count={owners.size} onClick={() => setMenu(menu === 'owner' ? null : 'owner')} />
          {!teamRoute && <Chip refEl={teamBtn} icon="square" label="团队" count={teams.size} onClick={() => setMenu(menu === 'team' ? null : 'team')} />}
          <Chip refEl={sigBtn} icon="pulse" label="健康信号" count={sig.size} onClick={() => setMenu(menu === 'signal' ? null : 'signal')} />
          {anyFilter ? (
            <button
              type="button"
              className="rd-clear"
              onClick={() => {
                setOwners(new Set());
                setSig(new Set());
                if (!teamRoute) setTeams(new Set());
              }}
            >
              <Icon name="close" size={12} />
              清除筛选
            </button>
          ) : (
            <button type="button" className="rd-clear" onClick={() => setMenu(menu === 'owner' ? null : 'owner')}>
              <Icon name="plus" size={12} stroke={1.5} />
              筛选
            </button>
          )}
        </div>
        <div className="row" style={{ gap: 8 }}>
          <div className="rd-seg" role="tablist" aria-label="视图">
            <button type="button" role="tab" aria-selected={view === 'list'} className={clsx(view === 'list' && 'on')} onClick={() => setView('list')}>
              <Icon name="list" size={13} stroke={1.4} color={view === 'list' ? 'var(--text-2)' : undefined} />
              列表
            </button>
            <button type="button" role="tab" aria-selected={view === 'board'} className={clsx(view === 'board' && 'on')} onClick={() => setView('board')}>
              <Icon name="board" size={13} color={view === 'board' ? 'var(--text-2)' : undefined} />
              看板
            </button>
          </div>
          <Chip refEl={displayBtn} icon="sliders" label={groupBy === 'status' ? '显示' : `分组：${groupBy === 'team' ? '团队' : '负责人'}`} onClick={() => setMenu(menu === 'display' ? null : 'display')} />
        </div>
      </div>

      <Popover anchor={ownerBtn} open={menu === 'owner'} onClose={() => setMenu(null)} width={220}>
        <MenuList
          search
          items={members.map((m) => ({ value: m.id, label: m.name, icon: <Avatar initial={m.initial} seed={m.name} />, hint: m.title ?? m.teamName }))}
          selected={owners}
          onPick={(v) => toggleIn(owners, v, setOwners)}
        />
      </Popover>
      <Popover anchor={teamBtn} open={menu === 'team'} onClose={() => setMenu(null)} width={200}>
        <MenuList
          items={(me?.teams ?? []).map((t) => ({ value: t.id, label: t.name, icon: <span className="wd-team-chip">{t.initial}</span> }))}
          selected={teams}
          onPick={(v) => toggleIn(teams, v, setTeams)}
        />
      </Popover>
      <Popover anchor={sigBtn} open={menu === 'signal'} onClose={() => setMenu(null)} width={200}>
        <MenuList items={SIGNAL_ITEMS} selected={sig} onPick={(v) => toggleIn(sig, v, setSig)} />
      </Popover>
      <Popover anchor={displayBtn} open={menu === 'display'} onClose={() => setMenu(null)} width={220} align="end">
        <div className="wd-pop-label">分组</div>
        <MenuList
          items={[
            { value: 'status' as GroupBy, label: '按状态' },
            { value: 'team' as GroupBy, label: '按团队' },
            { value: 'owner' as GroupBy, label: '按负责人' },
          ]}
          selected={groupBy}
          onPick={(v) => setGroupBy(v)}
        />
        <div className="wd-pop-sep" />
        <div className="wd-pop-item" style={{ cursor: 'default' }}>
          <span className="flex1">显示已完成</span>
          <Toggle on={showDone} onChange={setShowDone} label="显示已完成" />
        </div>
      </Popover>

      {filtered.length === 0 ? (
        <EmptyState icon="grid" title={scope === 'mine' ? '你还没有参与的项目' : '没有符合条件的项目'} desc={anyFilter ? '换个筛选条件试试。' : scope === 'mine' ? emptyMineDesc : undefined}>
          {!anyFilter && scope === 'mine' && (
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              新建项目
            </Button>
          )}
        </EmptyState>
      ) : view === 'board' ? (
        <Board groups={groups.map((g) => ({ ...g, folded: false }))} onOpen={open} />
      ) : (
        <div className="rd-list">
          {groups.map((g) => {
            const isCollapsed = g.folded || collapsed.has(g.key);
            const limit = groupBy !== 'status' && !expandedTeams.has(g.key) ? 5 : Infinity;
            return (
              <div key={g.key}>
                <button
                  type="button"
                  className="rd-group"
                  aria-expanded={!isCollapsed}
                  onClick={() => (g.key === 'done' && groupBy === 'status' ? setShowDone(!showDone) : toggleIn(collapsed, g.key, setCollapsed))}
                >
                  <Icon name={isCollapsed ? 'chevR' : 'chevD'} size={10} stroke={1.8} />
                  {g.icon}
                  <span className="name">{g.label}</span>
                  <span>{g.items.length}</span>
                  {g.meta && isCollapsed && (
                    <>
                      <span className="flex1" />
                      <span className="t-meta">{g.meta}</span>
                    </>
                  )}
                </button>
                {!isCollapsed &&
                  g.items.slice(0, limit).map((p) => (
                    <Row key={p.id} p={p} selected={visibleRows[sel]?.id === p.id} onOpen={() => open(p)} onHover={() => setSel(visibleRows.findIndex((r) => r.id === p.id))} hideLatest={layout === 'rail'} />
                  ))}
                {!isCollapsed && g.items.length > limit && (
                  <button type="button" className="rd-more" onClick={() => toggleIn(expandedTeams, g.key, setExpandedTeams)}>
                    显示全部 {g.items.length} 个
                  </button>
                )}
              </div>
            );
          })}
          {foldedNote && <div className="rd-note">{foldedNote}</div>}
        </div>
      )}

      <div className="rd-foot">
        <Hint keys={['J', 'K']}>上下移动</Hint>
        <Hint keys={['↵']}>打开项目</Hint>
        <Hint keys={['F']}>筛选</Hint>
        <Hint keys={['⌘', 'B']}>切换看板</Hint>
      </div>
    </>
  );
}

function Chip({ refEl, icon, label, count, onClick }: { refEl: React.RefObject<HTMLButtonElement | null>; icon: IconName; label: string; count?: number; onClick: () => void }) {
  return (
    <button ref={refEl} type="button" className={clsx('rd-chip', count ? 'on' : undefined)} onClick={onClick}>
      <Icon name={icon} size={14} />
      {label}
      {count ? <span className="n">{count}</span> : null}
    </button>
  );
}

function Row({ p, selected, onOpen, onHover, hideLatest }: { p: RadarProjectDTO; selected: boolean; onOpen: () => void; onHover: () => void; hideLatest: boolean }) {
  const s = signals(p)[0];
  return (
    <div className={clsx('rd-row', selected && 'sel')} role="link" tabIndex={-1} onClick={onOpen} onMouseEnter={onHover}>
      <StatusIcon status={p.status} />
      <span className="code">{p.code}</span>
      <span className="name">{p.name}</span>
      <Avatar initial={p.ownerInitial} seed={p.ownerName} />
      {hideLatest ? <span className="flex1" /> : <span className={clsx('latest', !p.latest && 'none')}>{p.latest?.text ?? '暂无进展'}</span>}
      <span className="health">{s?.label ?? ''}</span>
      <span className="due">{p.dueDate ? fmtMonthDay(p.dueDate) : '—'}</span>
    </div>
  );
}

function Board({ groups, onOpen }: { groups: Group[]; onOpen: (p: RadarProjectDTO) => void }) {
  return (
    <div className="rd-board">
      {groups.map((g) => (
        <div key={g.key} className="rd-col">
          <div className="rd-col-head">
            {g.icon}
            <span className="name">{g.label}</span>
            <span>{g.items.length}</span>
          </div>
          <div className="rd-col-body">
            {g.items.map((p) => {
              const s = signals(p)[0];
              return (
                <button key={p.id} type="button" className="rd-card" onClick={() => onOpen(p)}>
                  <span className="row" style={{ gap: 8 }}>
                    <span className="t-meta">{p.code}</span>
                    <span className="flex1" />
                    {s && <span className="health">{s.label}</span>}
                  </span>
                  <span className="name">{p.name}</span>
                  {p.latest && <span className="latest">{p.latest.text}</span>}
                  <span className="row" style={{ gap: 8, marginTop: 2 }}>
                    <Avatar initial={p.ownerInitial} seed={p.ownerName} />
                    <span className="flex1" />
                    <span className="t-meta">{p.dueDate ? fmtMonthDay(p.dueDate) : ''}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
